/*
 * Copyright © 2020-2026 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 主状态管理 (RTK Slice)
 * @LastEditors: refactor
 * @LastEditTime: 2026-03-25
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import type { InstallerStatus } from '../utils/desktopLib'

// 用户类型
interface User {
  user_id: string
  nickname?: string
  headimgurl?: string
  mobile?: string
  groups?: any[]
  status?: number
  [key: string]: any
}

// 支付状态类型
interface PaymentStatus {
  visible: boolean
  sourceDetail: string
  vipKind: number // 1: 默认选中第一个价格，2: 默认选中svip价格
  isFromToView: boolean
  onlySvip?: boolean
  onlyVVip?: boolean
  /** 卡点来源驱动的动态文案插槽数据 · null/缺省 = 硬编码兜底 */
  customContent?: { variant: string; data: any } | null
}

// AI 聊天抽屉状态类型
interface AiChatDrawerStatus {
  visible: boolean
  info: Record<string, any>
}

// 客户代理抽屉状态类型
interface AgentCustomerDrawerStatus {
  visible: boolean
  info: Record<string, any>
}

// AB 测试配置类型
interface ABTestConfig {
  PhotoEnhancementAB: boolean
  buildingSafetyAIModelName: string
  buildingSafetyEntries: any[]
  buildingSafetyIndustryCode: any[]
  WatermarkBatchEditor: boolean
  groupWebVipAB: {
    entry: boolean
    benifitPage: boolean
    chargePage: boolean
    upgrade: boolean
  }
  // True=神策实验组，顶部走新版会员入口 banner + hover 卡；False=对照组保留旧入口
  webVipEntrance: boolean
  // True=神策实验组，左下角展示存储常驻入口卡；False=对照组不展示
  webStorageEntrance: boolean
  // True=命中神策“当前用户在 AI 填表员首页无历史点击”教育实验；False=维持原首页
  aiFormFillingOnboardingExperiment: boolean
  // True=命中初稿完成后的协作教育实验；False=维持原工作台交互
  aiFormFillingWorkbenchEducationExperiment: boolean
  // True=命中两次模板体验后的自有表格迁移实验；False=维持原模板区
  aiFormFillingOwnTemplateExperiment: boolean
  [key: string]: any
}

// OSS Token 类型
interface OSSToken {
  'net-cloud'?: {
    expire: number
    [key: string]: any
  }
  'fe-xhey'?: {
    expire: number
    [key: string]: any
  }
  [key: string]: any
}

// 模块类型
interface PathModule {
  path: string
  title: string
  iconImg?: any
  iconFont?: string
  children?: PathModule[]
  new?: boolean
  isExpired?: boolean
}

// State 类型
interface MainState {
  loading: boolean
  initialRequestFinished: boolean
  platformSignModalVisible: boolean
  paymentStatus: PaymentStatus
  aiChatDrawerStatus: AiChatDrawerStatus
  agentCustomerDrawerStatus: AgentCustomerDrawerStatus
  config: Record<string, any>
  isDesktop: boolean
  downloadSettingModalVisible: boolean
  hasNewNotify: boolean
  /** 顶栏通知面板是否展开，供业务页面主动唤起（如付费验真提交成功后引导查看消息通知） */
  notifyPanelVisible: boolean
  abTestConfig: ABTestConfig
  user: User | null
  activeModules: PathModule[] | null
  isCheckingAuth?: boolean
  ossToken?: OSSToken | null
  /** AI 电脑版安装包的后台预下载状态，null = 客户端不支持或还没查到 */
  desktopInstallerStatus: InstallerStatus | null
}

const initialState: MainState = {
  loading: false,
  initialRequestFinished: false,
  platformSignModalVisible: false,
  paymentStatus: {
    visible: false,
    sourceDetail: '',
    vipKind: 1,
    isFromToView: false
  },
  aiChatDrawerStatus: {
    visible: false,
    info: {}
  },
  agentCustomerDrawerStatus: {
    visible: false,
    info: {}
  },
  config: {},
  isDesktop: typeof window !== 'undefined' && !!(window as any).todayCameraAPI,
  downloadSettingModalVisible: false,
  hasNewNotify:
    typeof localStorage !== 'undefined' &&
    !!localStorage.getItem('hasNewNotify'),
  notifyPanelVisible: false,
  abTestConfig: {
    PhotoEnhancementAB: false,
    buildingSafetyAIModelName: '3B+10B_v0.3',
    buildingSafetyEntries: [],
    buildingSafetyIndustryCode: [],
    WatermarkBatchEditor: false,
    groupWebVipAB: {
      entry: false,
      benifitPage: false,
      chargePage: false,
      upgrade: false
    },
    // 由 App.tsx handleFetchABTestConfig 登录后拉取覆盖（见 useAbFlag('webVipEntrance')）
    webVipEntrance: false,
    // 由 App.tsx handleFetchABTestConfig 登录后拉取覆盖（见 useAbFlag('webStorageEntrance')）
    webStorageEntrance: false,
    aiFormFillingOnboardingExperiment: false,
    aiFormFillingWorkbenchEducationExperiment: false,
    aiFormFillingOwnTemplateExperiment: false
  },
  user: null,
  activeModules: null,
  isCheckingAuth: false,
  ossToken: null,
  desktopInstallerStatus: null
}

const mainSlice = createSlice({
  name: 'main',
  initialState,
  reducers: {
    // 设置用户信息（对应 SET_USER）
    setUser(
      state,
      action: PayloadAction<{
        user: User | null
        activeModules: PathModule[] | null
      } | null>
    ) {
      if (action.payload) {
        state.user = action.payload.user
        state.activeModules = action.payload.activeModules
      } else {
        state.user = null
        state.activeModules = null
      }
    },

    // 设置是否正在检查授权（对应 IS_CHECKING_AUTH）
    setIsCheckingAuth(state, action: PayloadAction<boolean>) {
      state.isCheckingAuth = action.payload
    },

    // 设置加载状态（对应 SET_LOADING）
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload
    },

    // 设置初始请求完成状态（对应 SET_INITIAL_REQUEST_FINISHED）
    setInitialRequestFinished(state, action: PayloadAction<boolean>) {
      state.initialRequestFinished = action.payload
    },

    // 设置 OSS Token（对应 SET_OSS_TOKEN）
    setOssToken(state, action: PayloadAction<OSSToken | null>) {
      state.ossToken = action.payload
    },

    // 设置配置（对应 SET_CONFIG）
    setConfig(
      state,
      action: PayloadAction<Array<{ name: string; value: any }>>
    ) {
      const config: Record<string, any> = {}
      for (let index = 0; index < action.payload.length; index++) {
        const item = action.payload[index]
        config[item.name] = item.value
      }
      state.config = config
    },

    // 清除状态（对应 CLEAR）
    clear() {
      return {
        ...initialState,
        // 重新计算 isDesktop 和 hasNewNotify
        isDesktop:
          typeof window !== 'undefined' && !!(window as any).todayCameraAPI,
        hasNewNotify:
          typeof localStorage !== 'undefined' &&
          !!localStorage.getItem('hasNewNotify')
      }
    },

    // 设置 AI 聊天抽屉状态（对应 SET_AI_CHAT_DRAWER_VISIBLE）
    setAiChatDrawerStatus(state, action: PayloadAction<AiChatDrawerStatus>) {
      state.aiChatDrawerStatus = action.payload
    },

    // 设置客户代理抽屉状态（对应 SET_AGENT_CUSTOMER_DRAWER_VISIBLE）
    setAgentCustomerDrawerStatus(
      state,
      action: PayloadAction<AgentCustomerDrawerStatus>
    ) {
      state.agentCustomerDrawerStatus = action.payload
    },

    // 设置支付状态（对应 SET_PAYMENT_VISIBLE）
    setPaymentStatus(state, action: PayloadAction<PaymentStatus>) {
      state.paymentStatus = action.payload
    },

    // 设置下载设置弹窗可见性（对应 SET_DOWNLOAD_SETTING_MODAL_VISIBLE）
    setDownloadSettingModalVisible(state, action: PayloadAction<boolean>) {
      state.downloadSettingModalVisible = action.payload
    },

    // 设置是否有新通知（对应 SET_HAS_NEW_NOTIFY）
    setHasNewNotify(state, action: PayloadAction<boolean>) {
      state.hasNewNotify = action.payload
    },

    // 展开/收起顶栏通知面板
    setNotifyPanelVisible(state, action: PayloadAction<boolean>) {
      state.notifyPanelVisible = action.payload
    },

    // 设置 AI 电脑版安装包的预下载状态
    setDesktopInstallerStatus(
      state,
      action: PayloadAction<InstallerStatus | null>
    ) {
      state.desktopInstallerStatus = action.payload
    },

    // 设置 AB 测试配置（对应 SET_AB_TEST_CONFIG，使用 merge 策略）
    setAbTestConfig(state, action: PayloadAction<Partial<ABTestConfig>>) {
      state.abTestConfig = {
        ...state.abTestConfig,
        ...action.payload
      }
    }
  },
  extraReducers: (builder) => {
    // 兼容旧的 dispatch({ type: SET_AB_TEST_CONFIG, data: {...} }) 调用
    builder
      .addMatcher(
        (action) => action.type === 'SET_AB_TEST_CONFIG',
        (state, action: any) => {
          state.abTestConfig = {
            ...state.abTestConfig,
            ...action.data
          }
        }
      )
      .addMatcher(
        (action) => action.type === 'SET_LOADING',
        (state, action: any) => {
          state.loading = action.data
        }
      )
      .addMatcher(
        (action) => action.type === 'SET_OSS_TOKEN',
        (state, action: any) => {
          state.ossToken = action.data
        }
      )
      .addMatcher(
        (action) => action.type === 'CLEAR',
        () => {
          return {
            ...initialState,
            isDesktop:
              typeof window !== 'undefined' && !!(window as any).todayCameraAPI,
            hasNewNotify:
              typeof localStorage !== 'undefined' &&
              !!localStorage.getItem('hasNewNotify')
          }
        }
      )
  }
})

export const {
  setUser,
  setIsCheckingAuth,
  setLoading,
  setInitialRequestFinished,
  setOssToken,
  setConfig,
  clear,
  setAiChatDrawerStatus,
  setAgentCustomerDrawerStatus,
  setPaymentStatus,
  setDownloadSettingModalVisible,
  setHasNewNotify,
  setNotifyPanelVisible,
  setDesktopInstallerStatus,
  setAbTestConfig
} = mainSlice.actions

export default mainSlice.reducer

// 类型导出，供组件使用
export type {
  MainState,
  User,
  PaymentStatus,
  AiChatDrawerStatus,
  AgentCustomerDrawerStatus,
  ABTestConfig,
  OSSToken
}
